import * as ExcelJS from "exceljs";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";
import { SpecificationEtapeMaster } from "../../../specifications/models/specification-etape.model";
import type { SpecRow } from "./specification-list-dialog.component";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  border: "FFD0D5DD",
  redigeeBg: "FFFFF7E6",
  valideeBg: "FFE6FFF5",
  rawHeader: "FFE5E7EB",
  titleColor: "FF1B2A4A",
};

interface Stats {
  total: number;
  redigees: number;
  validees: number;
  mostRecentDenom: string;
  mostRecentDate: Date | null;
}

export interface SpecificationListExcelData {
  rows: SpecRow[];
  allSpecifications?: SpecificationEtapeMaster[];
  /** Résolveur de denomination depuis articleId — fournit la denomination article
   *  pour les specs qui n'ont pas leur propre denomination. */
  resolveDenom?: (articleId: string) => string | null;
  resolveCode?: (articleId: string) => string | null;
  stats: Stats;
  scope: string;
}

export async function exportSpecificationListExcel(
  data: SpecificationListExcelData,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, { title: "Spécifications" });

  await buildSyntheseSheet(wb, data);
  await buildListSheet(wb, data);
  await buildParArticleSheet(wb, data);
  await buildParMoisSheet(wb, data);
  await buildRawSheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `specifications_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function denomOf(
  s: SpecificationEtapeMaster,
  resolve?: (id: string) => string | null,
): string {
  if (s.denomination) return s.denomination;
  if (s.articleId && resolve) {
    const d = resolve(s.articleId);
    if (d) return d;
  }
  return "—";
}

function codeOf(
  s: SpecificationEtapeMaster,
  resolve?: (id: string) => string | null,
): string {
  if (s.articleId && resolve) {
    const c = resolve(s.articleId);
    if (c) return c;
  }
  return "";
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildSyntheseSheet(
  wb: ExcelJS.Workbook,
  data: SpecificationListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Synthèse");
  ws.columns = [{ width: 36 }, { width: 28 }, { width: 12 }];

  setSheetTitle(ws, `Spécifications — ${data.scope}`, 3);
  await addJustupBranding(wb, ws, 3);

  let row = 3;
  ws.getCell(row, 1).value = "Indicateurs clés";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  const ratio =
    data.stats.total > 0 ? data.stats.validees / data.stats.total : 0;
  const kpis: Array<[string, string | number]> = [
    ["Total versions affichées", data.stats.total],
    ["Rédigées", data.stats.redigees],
    ["Validées", data.stats.validees],
    [
      "Taux de validation",
      data.stats.total > 0 ? `${(ratio * 100).toFixed(0)} %` : "—",
    ],
    [
      "Plus récente",
      data.stats.mostRecentDate
        ? `${data.stats.mostRecentDenom} (${formatDate(data.stats.mostRecentDate)})`
        : "—",
    ],
  ];
  for (const [label, value] of kpis) {
    ws.getCell(row, 1).value = label;
    ws.getCell(row, 1).font = { bold: true };
    ws.getCell(row, 2).value = value;
    row++;
  }

  // Articles avec spécif rédigée mais sans version validée
  const allSpec = data.allSpecifications ?? [];
  const redigeesNonValidees = allSpec.filter((s) => {
    const versions = s.versions ?? [];
    const hasRedigee = versions.some((v) => v.etat === "REDIGEE");
    const hasValidee = versions.some(
      (v) => v.etat === "VALIDEE" || (v.etat as string) === "APPROUVEE",
    );
    return hasRedigee && !hasValidee;
  }).length;

  row += 1;
  ws.getCell(row, 1).value = "Articles avec rédaction non validée";
  ws.getCell(row, 1).font = { bold: true };
  ws.getCell(row, 2).value = redigeesNonValidees;
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildListSheet(
  wb: ExcelJS.Workbook,
  data: SpecificationListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Liste");
  ws.columns = [
    { key: "denomination", width: 36 },
    { key: "code", width: 16 },
    { key: "version", width: 10 },
    { key: "etat", width: 14 },
    { key: "date", width: 14 },
  ];
  setSheetTitle(ws, `Spécifications — ${data.scope}`, ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Article",
    "Code article",
    "Version",
    "État",
    "Date",
  ]);

  for (const row of data.rows) {
    const r = ws.addRow({
      denomination: row.denomination,
      code: row.code ?? "",
      version: row.versionNb ?? "",
      etat: row.etat === "REDIGEE" ? "Rédigée" : "Validée",
      date: row.date,
    });
    r.getCell("date").numFmt = "dd/mm/yyyy";
    r.getCell("etat").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: row.etat === "VALIDEE" ? C.valideeBg : C.redigeeBg },
    };
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildParArticleSheet(
  wb: ExcelJS.Workbook,
  data: SpecificationListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Par article");
  ws.columns = [
    { key: "article", width: 36 },
    { key: "code", width: 16 },
    { key: "nbVersions", width: 14 },
    { key: "lastVer", width: 16 },
    { key: "lastEtat", width: 16 },
    { key: "lastDate", width: 14 },
    { key: "firstRedac", width: 18 },
    { key: "firstValid", width: 18 },
    { key: "delai", width: 20 },
  ];
  setSheetTitle(ws, "Spécifications — agrégation par article", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Article",
    "Code article",
    "Nb versions",
    "Dernière version",
    "État dernière",
    "Dernière date",
    "Première rédaction",
    "Première validation",
    "Délai validation (j)",
  ]);

  type Agg = {
    article: string;
    code: string;
    versions: Array<{
      version_nb: number | null;
      etat: string;
      date: Date | null;
      dateRedaction: Date | null;
      dateValidation: Date | null;
    }>;
  };
  const map = new Map<string, Agg>();
  const source = data.allSpecifications ?? [];

  for (const s of source) {
    const denom = denomOf(s, data.resolveDenom);
    const code = codeOf(s, data.resolveCode);
    const key = `${denom}::${code}`;
    const agg = map.get(key) ?? { article: denom, code, versions: [] };
    for (const v of s.versions ?? []) {
      agg.versions.push({
        version_nb: v.version_nb ?? null,
        etat: v.etat as string,
        date: ((v as any).dateValidation ?? v.dateRedaction ?? null) as
          | Date
          | null,
        dateRedaction: v.dateRedaction
          ? new Date(v.dateRedaction as any)
          : null,
        dateValidation: (v as any).dateValidation
          ? new Date((v as any).dateValidation)
          : null,
      });
    }
    map.set(key, agg);
  }

  const rows = [...map.values()].sort((a, b) =>
    a.article.localeCompare(b.article),
  );
  for (const a of rows) {
    a.versions.sort((x, y) => (x.version_nb ?? 0) - (y.version_nb ?? 0));
    const last = a.versions[a.versions.length - 1];
    const firstRedac = a.versions
      .map((v) => v.dateRedaction)
      .filter((d): d is Date => !!d)
      .sort((x, y) => x.getTime() - y.getTime())[0];
    const firstValid = a.versions
      .map((v) => v.dateValidation)
      .filter((d): d is Date => !!d)
      .sort((x, y) => x.getTime() - y.getTime())[0];
    const delai =
      firstRedac && firstValid
        ? Math.round(
            (firstValid.getTime() - firstRedac.getTime()) /
              (1000 * 60 * 60 * 24),
          )
        : "";

    const r = ws.addRow({
      article: a.article,
      code: a.code,
      nbVersions: a.versions.length,
      lastVer: last?.version_nb ?? "",
      lastEtat: last?.etat ?? "",
      lastDate: last?.date ?? null,
      firstRedac: firstRedac ?? null,
      firstValid: firstValid ?? null,
      delai,
    });
    r.getCell("lastDate").numFmt = "dd/mm/yyyy";
    r.getCell("firstRedac").numFmt = "dd/mm/yyyy";
    r.getCell("firstValid").numFmt = "dd/mm/yyyy";
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildParMoisSheet(
  wb: ExcelJS.Workbook,
  data: SpecificationListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Par mois");
  ws.columns = [
    { key: "mois", width: 14 },
    { key: "redactions", width: 14 },
    { key: "validations", width: 14 },
    { key: "solde", width: 12 },
  ];
  setSheetTitle(ws, "Spécifications — évolution mensuelle", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Mois",
    "Nb rédactions",
    "Nb validations",
    "Solde net",
  ]);

  const buckets = new Map<string, { red: number; val: number }>();
  for (const s of data.allSpecifications ?? []) {
    for (const v of s.versions ?? []) {
      if (v.dateRedaction) {
        const key = monthKey(v.dateRedaction);
        const b = buckets.get(key) ?? { red: 0, val: 0 };
        b.red++;
        buckets.set(key, b);
      }
      if ((v as any).dateValidation) {
        const key = monthKey((v as any).dateValidation);
        const b = buckets.get(key) ?? { red: 0, val: 0 };
        b.val++;
        buckets.set(key, b);
      }
    }
  }
  const ordered = [...buckets.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  for (const [key, b] of ordered) {
    ws.addRow({
      mois: key,
      redactions: b.red,
      validations: b.val,
      solde: b.val - b.red,
    });
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildRawSheet(
  wb: ExcelJS.Workbook,
  data: SpecificationListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Données brutes");
  ws.columns = [
    { key: "specId", width: 38 },
    { key: "specDenom", width: 32 },
    { key: "articleId", width: 38 },
    { key: "articleCode", width: 16 },
    { key: "isActive", width: 10 },
    { key: "versionId", width: 38 },
    { key: "versionNb", width: 10 },
    { key: "etat", width: 16 },
    { key: "dateRed", width: 14 },
    { key: "dateVal", width: 14 },
    { key: "dateApp", width: 14 },
    { key: "createdAt", width: 18 },
    { key: "updatedAt", width: 18 },
  ];
  setSheetTitle(ws, "Données brutes — toutes les spécifications", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  const headerRow = ws.getRow(2);
  headerRow.values = [
    "spec.id",
    "spec.denomination",
    "spec.articleId",
    "article.code",
    "spec.isActive",
    "version.id",
    "version_nb",
    "etat",
    "dateRedaction",
    "dateValidation",
    "dateApprobation",
    "createDateTime",
    "lastChangedDateTime",
  ];
  headerRow.eachCell((c) => {
    c.font = { bold: true, size: 11 };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.rawHeader } };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  });

  for (const s of data.allSpecifications ?? []) {
    const denom = denomOf(s, data.resolveDenom);
    const code = codeOf(s, data.resolveCode);
    for (const v of s.versions ?? []) {
      const r = ws.addRow({
        specId: s.id ?? "",
        specDenom: denom,
        articleId: s.articleId ?? "",
        articleCode: code,
        isActive: (s as any).isActive ?? "",
        versionId: (v as any).id ?? "",
        versionNb: v.version_nb ?? "",
        etat: v.etat ?? "",
        dateRed: v.dateRedaction ? new Date(v.dateRedaction as any) : null,
        dateVal: (v as any).dateValidation
          ? new Date((v as any).dateValidation)
          : null,
        dateApp: (v as any).dateApprobation
          ? new Date((v as any).dateApprobation)
          : null,
        createdAt: (v as any).createDateTime
          ? new Date((v as any).createDateTime)
          : null,
        updatedAt: (v as any).lastChangedDateTime
          ? new Date((v as any).lastChangedDateTime)
          : null,
      });
      r.getCell("dateRed").numFmt = "dd/mm/yyyy";
      r.getCell("dateVal").numFmt = "dd/mm/yyyy";
      r.getCell("dateApp").numFmt = "dd/mm/yyyy";
      r.getCell("createdAt").numFmt = "dd/mm/yyyy hh:mm";
      r.getCell("updatedAt").numFmt = "dd/mm/yyyy hh:mm";
    }
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
function setSheetTitle(
  ws: ExcelJS.Worksheet,
  title: string,
  ncols: number,
): void {
  const row = ws.getRow(1);
  row.height = 22;
  ws.mergeCells(1, 1, 1, Math.max(1, ncols - 1));
  row.getCell(1).value = title;
  row.getCell(1).font = { bold: true, size: 14, color: { argb: C.titleColor } };
  row.getCell(1).alignment = { vertical: "middle" };
}

function styleHeader(
  row: ExcelJS.Row,
  ncols: number,
  values?: string[],
): void {
  if (values) {
    for (let i = 0; i < values.length; i++) {
      row.getCell(i + 1).value = values[i];
    }
  }
  row.height = 20;
  for (let i = 1; i <= ncols; i++) {
    const c = row.getCell(i);
    c.font = { bold: true, color: { argb: C.headerFont } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
    c.alignment = { vertical: "middle" };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  }
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("fr-FR");
}

function monthKey(d: Date | string): string {
  const date = d instanceof Date ? d : new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
